<template>
  <div class="audio-player">
    <div class="audio-toggle">
      <span :class="{ active: !isMastered }">Original</span>
      <label class="switch">
        <input type="checkbox" :checked="isMastered" @change="handleToggle" />
        <span class="slider round"></span>
      </label>
      <span :class="{ active: isMastered }">Bearbeitet</span>
    </div>

    <div class="player-controls">
      <button @click="togglePlay" class="play-pause-btn">
        <svg
          v-if="!isPlaying"
          width="32"
          height="32"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style="margin-left: 2px"
        >
          <path d="M5 3L17 10L5 17V3Z" fill="currentColor" />
        </svg>
        <svg
          v-else
          width="32"
          height="32"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="5" y="3" width="3" height="14" fill="currentColor" />
          <rect x="12" y="3" width="3" height="14" fill="currentColor" />
        </svg>
      </button>

      <div class="progress-container">
        <div class="time-display">{{ formatTime(currentTime) }}</div>
        <div class="progress-bar" @click="seek" ref="progressBar">
          <div
            class="progress"
            :style="{ width: progressPercentage + '%' }"
          ></div>
          <div
            class="progress-handle"
            :style="{ left: progressPercentage + '%' }"
          ></div>
        </div>
        <div class="time-display">{{ formatTime(duration) }}</div>
      </div>

      <div class="volume-control">
        <button @click="toggleMute" class="volume-btn">
          <svg
            v-if="isMuted || volume === 0"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.76C15.38 20.45 16.63 19.81 17.68 18.95L19.73 21L21 19.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z"
              fill="currentColor"
            />
          </svg>
          <svg
            v-else
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.02C15.48 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>

    <audio
      ref="audioMastered"
      :src="src"
      @timeupdate="updateTime"
      @loadedmetadata="updateDuration"
      @ended="onEnded"
      @canplay="onCanPlay"
    ></audio>
    <audio
      ref="audioOriginal"
      :src="originalSrc || src"
      @timeupdate="updateTime"
      @loadedmetadata="updateDuration"
      @ended="onEnded"
      @canplay="onCanPlay"
    ></audio>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  originalSrc: {
    type: String,
    default: "",
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
});

const audioMastered = ref(null);
const audioOriginal = ref(null);
const progressBar = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(1);
const isMuted = ref(false);
const isMastered = ref(true);
const audiosReady = ref(0);
let animationFrame = null;

const progressPercentage = ref(0);

const togglePlay = async () => {
  if (isPlaying.value) {
    // Pause both audios
    if (audioMastered.value) audioMastered.value.pause();
    if (audioOriginal.value) audioOriginal.value.pause();
    isPlaying.value = false;
  } else {
    // Ensure only the active audio plays
    const activeAudio = isMastered.value
      ? audioMastered.value
      : audioOriginal.value;

    if (activeAudio) {
      try {
        // Set correct volume before starting playback
        updateVolume();

        // Only play the active audio
        await activeAudio.play();
        isPlaying.value = true;
      } catch (error) {
        console.error("Error playing audio:", error);
        isPlaying.value = false;
      }
    }
  }
};

const updateTime = () => {
  const selectedAudio = isMastered.value
    ? audioMastered.value
    : audioOriginal.value;
  if (selectedAudio) {
    currentTime.value = selectedAudio.currentTime;

    // Use the shorter audio duration for progress calculation
    const masteredDuration = audioMastered.value?.duration || 0;
    const originalDuration = audioOriginal.value?.duration || 0;
    const shorterDuration =
      Math.min(masteredDuration, originalDuration) || selectedAudio.duration;

    progressPercentage.value = (currentTime.value / shorterDuration) * 100;
  }

  if (isPlaying.value) {
    animationFrame = requestAnimationFrame(updateTime);
  }
};

const updateDuration = () => {
  const selectedAudio = isMastered.value
    ? audioMastered.value
    : audioOriginal.value;
  if (selectedAudio && selectedAudio.duration) {
    duration.value = selectedAudio.duration;
  }
};

const seek = (e) => {
  if (!audioMastered.value || !audioOriginal.value) return;

  const rect = progressBar.value.getBoundingClientRect();
  const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  const newTime = pos * duration.value;

  // Update both audio elements to maintain sync
  const wasPlaying = isPlaying.value;

  // Pause both audios if playing to prevent glitches
  if (wasPlaying) {
    audioMastered.value.pause();
    audioOriginal.value.pause();
  }

  // Set the new time for both audios
  audioMastered.value.currentTime = newTime;
  audioOriginal.value.currentTime = newTime;

  // Update the current time display
  currentTime.value = newTime;

  // Resume playing if we were playing before seeking
  if (wasPlaying) {
    const playPromises = [
      audioMastered.value.play(),
      audioOriginal.value.play(),
    ];

    // Handle any play promise errors
    Promise.all(
      playPromises.map((p) => p.catch((e) => console.warn("Play error:", e))),
    );
  }
};

const updateVolume = () => {
  if (!audioMastered.value || !audioOriginal.value) return;

  // Set volumes directly - we'll handle the fade in the toggle
  if (isMastered.value) {
    audioMastered.value.volume = isMuted.value ? 0 : volume.value;
    audioOriginal.value.volume = 0;
  } else {
    audioMastered.value.volume = 0;
    audioOriginal.value.volume = isMuted.value ? 0 : volume.value;
  }
};

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  updateVolume();
};

const onEnded = () => {
  isPlaying.value = false;
  if (audioMastered.value) audioMastered.value.pause();
  if (audioOriginal.value) audioOriginal.value.pause();
  cancelAnimationFrame(animationFrame);
};

const onCanPlay = () => {
  audiosReady.value++;
  if (audiosReady.value >= 2) {
    updateVolume();
  }
};

async function handleToggle() {
  try {
    // Get current time and playing state before toggling
    const currentAudio = isMastered.value
      ? audioMastered.value
      : audioOriginal.value;
    const currentTime = currentAudio?.currentTime || 0;
    const wasPlaying = isPlaying.value;

    // Pause current audio if playing
    if (wasPlaying && currentAudio) {
      currentAudio.pause();
    }

    // Toggle the source
    isMastered.value = !isMastered.value;

    // Get the new active audio
    const newAudio = isMastered.value
      ? audioMastered.value
      : audioOriginal.value;

    if (newAudio) {
      // Set the time on the new audio
      newAudio.currentTime = currentTime;

      // Update volumes (will handle the crossfade)
      updateVolume();

      // If it was playing, start the new audio
      if (wasPlaying) {
        try {
          await newAudio.play();
        } catch (err) {
          console.error("Error playing audio:", err);
        }
      }
    }
  } catch (error) {
    console.error("Error in handleToggle:", error);
  }
}

const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Remove the toggleAudioSource function as it's no longer needed
// The functionality has been moved to handleToggle

onMounted(async () => {
  // Set initial volumes to 0 to prevent both from playing
  if (audioMastered.value) audioMastered.value.volume = 0;
  if (audioOriginal.value) audioOriginal.value.volume = 0;

  // Wait for both audio elements to be ready
  const waitForAudioReady = (audio) => {
    return new Promise((resolve) => {
      if (audio.readyState >= 2) {
        // HAVE_CURRENT_DATA
        resolve(true);
      } else {
        const onCanPlay = () => {
          audio.removeEventListener("canplay", onCanPlay);
          resolve(true);
        };
        audio.addEventListener("canplay", onCanPlay);
      }
    });
  };

  // Wait for both audio elements to be ready
  await Promise.all([
    audioMastered.value
      ? waitForAudioReady(audioMastered.value)
      : Promise.resolve(),
    audioOriginal.value
      ? waitForAudioReady(audioOriginal.value)
      : Promise.resolve(),
  ]);

  // Set initial volumes based on the current state
  updateVolume();

  // Initial duration update
  updateDuration();

  // Watch for changes to the isMastered state and update volume accordingly
  watch(
    () => isMastered.value,
    () => {
      updateVolume();
      updateDuration();
    },
    { immediate: true },
  );

  // Initial volume setup
  updateVolume();

  // Watch for changes to the isMuted state and update volume accordingly
  watch(
    () => isMuted.value,
    () => {
      updateVolume();
    },
  );

  // If autoplay is enabled, start both audios
  if (props.autoplay) {
    setTimeout(() => {
      if (audioMastered.value && audioOriginal.value) {
        audioMastered.value.play().catch((error) => {
          console.error("Autoplay failed for mastered:", error);
        });
        audioOriginal.value.play().catch((error) => {
          console.error("Autoplay failed for original:", error);
        });
        isPlaying.value = true;
      }
    }, 100);
  }

  // Clean up animation frame on unmount
  onBeforeUnmount(() => {
    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }
  });
});

// Watch for changes to the src and originalSrc props
watch(
  () => props.src,
  (newSrc) => {
    if (audioMastered.value) {
      audioMastered.value.src = newSrc;
    }
  },
);

watch(
  () => props.originalSrc,
  (newSrc) => {
    if (audioOriginal.value) {
      audioOriginal.value.src = newSrc;
    }
  },
);
</script>

<style scoped>
.audio-player {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.play-pause-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #222;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.play-pause-btn:hover {
  background: #333;
  transform: scale(1.05);
}

.play-pause-btn:active {
  transform: scale(0.95);
}

.progress-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.time-display {
  font-size: 14px;
  color: #6b7280;
  min-width: 40px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  overflow: visible;
}

.progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: black;
  border-radius: 2px;
  transition: width 0.1s linear;
  pointer-events: none;
}

.progress-handle {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  background: black;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  pointer-events: none;
}

.progress-bar:hover .progress-handle {
  opacity: 1;
}

.volume-control {
  display: flex;
  align-items: center;
}

.volume-btn {
  background: none;
  border: none;
  color: black;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.volume-btn:hover {
  background: #f3f4f6;
}

.audio-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #6b7280;
}

.audio-toggle span {
  transition: color 0.3s ease;
}

.audio-toggle span.active {
  color: black;
  font-weight: 500;
}

/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e7eb;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

input:checked + .slider {
  background-color: black;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>
